import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_SOURCES_FOR_PROJECT } from '../../../../query/monitor/data/sources/keys';
import { getSourcesForProject } from '../../../../../services/monitor/data/sources/MonitorDataSourcesService';
import { SourceDataResponse } from '../../../../../models/monitor/data/sources/SourceDataResponse';
import { ChatType } from '../../../../../types/monitor/ChatType';
import { MonitorPrompt } from '../../../../../models/monitor/MonitorPrompt';
import { useMemo } from 'react';

export const useGetSourcesForProject = (
  projectId: number | null,
  filteredMonitorPrompts: MonitorPrompt[] | null,
  chatType: ChatType | null,
  from: Date | null,
  to: Date | null,
  filterMonitorTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const hasPrompts = !!filteredMonitorPrompts && filteredMonitorPrompts.length > 0;
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && hasPrompts;

  const monitorPromptIds = useMemo(
    () => filteredMonitorPrompts?.map((p) => p.id).filter((id): id is number => id != null),
    [filteredMonitorPrompts]
  );

  const {
    data: sourcesForProject,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<SourceDataResponse>({
    queryKey: [GET_SOURCES_FOR_PROJECT, projectId, chatType, from, to, monitorPromptIds, filterMonitorTypes],
    queryFn: () => getSourcesForProject(projectId!, chatType!, from, to, monitorPromptIds, filterMonitorTypes),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  return {
    sourcesForProject: queryEnabled
      ? sourcesForProject ?? {
          linksData: [],
          domains: [],
        }
      : {
          linksData: [],
          domains: [],
        },
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_SOURCES_FOR_PROJECT, projectId, chatType, from, to],
        refetchType: 'active',
      }),
  };
};
